import { autoinject, BindingEngine, computedFrom, customElement } from 'aurelia-framework';
import { ValidationRules } from 'aurelia-validation';
import { DeviationRequestFormCustomElement } from "components/deviation-request-form";
import { EntityWideDeviationRequest } from "model/forms/deviation-request";
import { EnumsService } from 'services/enums-service';
import { KeywordsService } from 'services/keywords-service';
import { LookupsService } from 'services/lookups-service';
import { MAX_DATE } from 'support/settings';

@autoinject
@customElement('entity-wide-deviation-request-form')
export class EntityWideDeviationRequestFormCustomElement extends DeviationRequestFormCustomElement<EntityWideDeviationRequest> {
  public today = new Date().toISOString().slice(0, 10);
  public maxDate = MAX_DATE.toISOString().slice(0, 10);

  constructor(bindingEngine: BindingEngine, protected lookups: LookupsService, protected keywords: KeywordsService, protected enums: EnumsService) {
    super(bindingEngine, lookups, keywords, enums);
  }

  modelChanged(newValue: EntityWideDeviationRequest) {
    super.modelChanged(newValue);
    ValidationRules
      .ensureObject<EntityWideDeviationRequest>().required()
      .ensure(model => model.requestedBy).displayName('Requested By').required()
      .ensure(model => model.organisationEntity).displayName('Organisation').required()
      .ensure(model => model.regionalEntityLeader).displayName('Regional Entity Leader').required()
      .ensure(model => model.isSupportedByRegionalEntityLeader).equals(true).withMessage('Approval from your Regional Entity Leader is required.')
      .ensure(model => model.isSupportedByEpra).satisfies(value => value != null && value === true).when(() => this.workflowStrategy.hasEpra).withMessage('Approval from your EPRA is required.') //workaround validation from DeviationRequestEpraPanelCustomElement
      .ensure(model => model.sORLeader).displayName('S&OR Leader').required().when(() => this.workflowStrategy.hasSORLeader)
      .ensure(model => model.isSupportedBySORLeader).satisfies(value => value != null && value === true).when(() => this.workflowStrategy.hasSORLeader).withMessage('Approval from your S&OR Leader is required.')
      .on(this.model);

    this.supportValidationController.addObject(this.model,
      ValidationRules.ensureObject<EntityWideDeviationRequest>().required()
        .ensure(model => model.validFrom).displayName("Valid From").required().satisfiesRule('maxDate')
        .ensure(model => model.validTo).displayName("Valid To").required().satisfiesRule('minDate').satisfiesRule('maxDate')
        .ensure(model => model.aviationCOE).displayName("Aviation COE").required().when(() => this.workflowStrategy.hasAviationCOE)
        .ensure(model => model.isSupportedByAviationCOE).satisfies(value => value != null && value === true).when(() => this.workflowStrategy.hasAviationCOE).withMessage('Support from Aviation COE is required.')
        .ensure(model => model.seniorVPHSE).displayName("Senior VP HSE").required().when(() => this.workflowStrategy.hasSeniorVPHSE)
        .ensure(model => model.isSupportedBySeniorVPHSE).satisfies(value => value != null && value === true).when(() => this.workflowStrategy.hasSeniorVPHSE).withMessage('Support from Senior VP HSE is required.')
        .ensure(model => model.cfo).displayName("CFO").required().when(() => this.workflowStrategy.hasCFO)
        .ensure(model => model.isSupportedByCFO).satisfies(value => value != null && value === true).when(() => this.workflowStrategy.hasCFO).withMessage('Support from CFO is required.')
        .rules
    )
  }

  @computedFrom('model.regionalEntityLeader')
  get regionalEntityLeaderGroupLabel() {
    return ['Regional Entity Leader / bp employee name', this.model.regionalEntityLeader?.mail, this.model.regionalEntityLeader?.phone].filter(l => l != null);
  }

  @computedFrom('model.sORLeader')
  get sORLeaderGroupLabel() {
    return ['S&OR Leader / bp employee name', this.model.sORLeader?.mail, this.model.sORLeader?.phone].filter(l => l != null);
  }
}
